import { initMixpanel, resetMixpanel, setMixpanelUser } from "@/utils/mixpanel";
import { initSmartlook } from "@/utils/smartlook";
import {
  isMixpanelEnabled,
  resolveTrackingPolicy,
} from "@/utils/tracking/resolve-tracking-permission";
import { syncAnalyticsEligibility } from "@/utils/tracking/sync-analytics-eligibility";
import { useAuth } from "@clerk/clerk-react";
import { PropsWithChildren, useEffect } from "react";

export const TrackingProvider = ({ children }: PropsWithChildren) => {
  const { isLoaded, isSignedIn, userId, getToken } = useAuth();

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) return;
    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const sync = async () => {
      try {
        const enabled = isMixpanelEnabled();
        if (controller.signal.aborted) return;
        const token = await getToken();
        if (controller.signal.aborted) return;
        if (!token) throw new Error("Authentication unavailable");
        await syncAnalyticsEligibility(enabled, token, controller.signal);
      } catch {
        if (controller.signal.aborted) return;
        if (++attempts < 4) {
          retry = setTimeout(() => void sync(), 2000 * 2 ** (attempts - 1));
        } else {
          console.warn("[Tracking] Could not sync analytics eligibility");
        }
      }
    };
    void sync();
    return () => {
      controller.abort();
      if (retry) clearTimeout(retry);
    };
  }, [getToken, isLoaded, isSignedIn, userId]);

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && userId) setMixpanelUser(userId);
    else if (!isSignedIn) resetMixpanel();
  }, [isLoaded, isSignedIn, userId]);

  useEffect(() => {
    if (!isLoaded) return;
    let isActive = true;
    const initTracking = async () => {
      try {
        await Promise.resolve();
        if (!isActive) return;
        // Separate promises also isolate synchronous SDK failures.
        await Promise.allSettled([
          Promise.resolve().then(() => initMixpanel(isMixpanelEnabled())),
          Promise.resolve().then(async () => {
            const policy = await resolveTrackingPolicy();
            if (isActive) await initSmartlook(policy.replayEnabled);
          }),
        ]);
      } catch {
        console.warn("[Tracking] Could not initialise tracking");
      }
    };
    void initTracking();
    return () => {
      isActive = false;
    };
  }, [isLoaded]);

  return <>{children}</>;
};
